import Backbone from 'backbone'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import DatasetsFilterBadges from 'scoreboard/datasets/filters/datasets-filter-badges'
import template from './dataset-filters.hbs'
import { isV5 } from 'scoreboard/components/version'

const DatasetFiltersView = Backbone.View.extend({
	className: 'panel filters',

	events: {
		'click .panel-header .add': 'addFilter'
	},

	/** @type {Dataset} */
	model: null,

	/**
	 * View that encapsulates the active & security filters for datasets & dataset reports
	 *
	 * @constructs
	 *
	 * @param {Dataset} options.model the Dataset
	 *
	 * @param {Object} options
	 *
	 * @param {Object} options.filters  all filters applied to the dataset (including security filters)
	 *
	 * @param {Object} options.treeItemsById  hash of tree items by id
	 *
	 * @param {Integer} [options.numResults]  number of filtered results
	 *
	 * @param {Integer} [options.maxResults]  number of all results
	 *
	 * @param {Boolean} [options.showDatasetLabel=false]  if true, the dataset
	 * label will be included in the panel header
	 *
	 * @param {Boolean} [options.allowAdd=true] whether the Add button will be displayed
	 *
	 * @param {Boolean} [options.includeEditFields=true] whether to include the
	 * "Record is Editable" and "Record Edited" fields when adding/editing filters
	 */
	initialize(options = {}) {
		this.options = options
		this.allowAdd = _.defaultTo(this.options.allowAdd,  true)
		this.includeEditFields = _.defaultTo(this.options.includeEditFields,  true)
		this.treeItemsById = this.options.treeItemsById || {}
		this.securityFilters = []
		this.setFilters(options.filters || [])
	},

	setFilters(filters = []) {
		this.filters = filters
		this.filtersByPermissionId = _.groupBy(this.filters, 'permissionId')
	},

	render() {
		this.renderPanel()
		this.renderShowingXRecords()
		this.renderActiveFilters()
		this.renderSecurityFilters()
		return this
	},

	renderPanel() {
		let header = i18n.get('datasets.records.activeFilters')
		let buttonType = isV5() ? 'button-covert-labeled' : '' // Fixes #58851
		if (this.options.showDatasetLabel) {
			header = this.model.get('label') + ' ' + header
		}

		if (this.options.header) {
			header = this.options.header
		}

		this.$el.html(template({
			header,
			buttonType
		}))

		this.$('.button.add').toggleClass('is-not-displayed', !this.allowAdd)
	},

	/**
	 * Renders the text showing the number of records returned after applying the current filters
	 * @param  {Integer} [data.numResults]  number of filtered results
	 * @param {Integer} [data.maxResults]  number of all results
	 */
	renderShowingXRecords(data={}) {
		_.extend(this.options, ({numResults, maxResults} = data))
		let recordTotals = ''
		let total = this.options.maxResults
		if (!_.isNil(total) && !_.isNil(this.options.numResults)) {
			recordTotals = this.options.numResults == total ?
				i18n.get('datasets.records.showingAll', total) :
				i18n.get('datasets.records.showingSomeOfTotal', this.options.numResults, total)
		}
		this.$('.showing-x-records').text(recordTotals)
	},

	renderActiveFilters() {
		this.activeFilters && this.activeFilters.remove()

		// Check if the view has security filters
		let hasOperator = (filter) => !_.isNil(filter.operator)
		let activeFilters = _.filter(this.filtersByPermissionId[undefined], hasOperator)
		let securityFilters = _.filter(this.filters, hasOperator)
		let numSecurityFilters = securityFilters.length - _.size(activeFilters)
		let hasSecurityFilters = numSecurityFilters != 0 && !this.hasUnfilteredPermission()

		this.activeFilters = new DatasetsFilterBadges({
			model: this.model,
			// show the filters that aren't security filters tied to a specific
			// permission
			filters: this.filtersByPermissionId[undefined] || [],
			treeItemsById: this.treeItemsById,
			showAggregationFilters: false,
			showPermissionFilters: false,
			// we want to support relative dates on the Datasets Explore tab to
			// support dynamic columns like SA per 1000 from #27466
			allowAppCalendar: true,
			hasSecurityFilters,
			includeEditFields: this.includeEditFields,
			noneMessage: this.options.noneMessage
		}).render()
		this.activeFilters.on('add-filter', filter => {
			this.filters = this.filters || []
			this.filters.push(filter)
			this.trigger('change', this.filters, { newFilter: filter })
			// a fresh query is triggered below
		})
		this.activeFilters.on('edit-filter', (newFilter, oldFilter, treeItems) => {
			_.each(this.filters, (filter, index) => {
				if (filter === oldFilter) {
					this.filters[index] = newFilter
					return false
				}
			})
			if (treeItems) {
				_.extend(this.treeItemsById, _.keyBy(treeItems, 'id'))
			}
			this.trigger('change', this.filters, { newFilter, oldFilter })
			// a fresh query is triggered below
		})
		this.activeFilters.on('delete-filter', deleteMe => {
			_.remove(this.filters, filter => {
				return filter == deleteMe
			})
			// a fresh query is triggered below
			this.trigger('change', this.filters, {
				newFilter: null,
				oldFilter: deleteMe
			})
			this.trigger('delete-filter', deleteMe)
		})
		this.$('.active-filters').html(this.activeFilters.el)
	},

	renderSecurityFilters() {
		// the server sends all filters relevant to the current user.  That
		// means any filters the user specified, any saved in the view, and
		// any security filters that apply to the user.  Filters that don't
		// apply to the user aren't included.  This gives us all the information
		// we need to figure out the permissionIds which are security filters
		// that apply to the current user
		let permissionIds = _(this.filters)
			.filter((filter) => !_.isNil(filter.permissionId))
			.map(filter => filter.permissionId)
			.uniq()
			.value()
		const permissionsById = _.keyBy(this.model.get('permissions'), 'id')
		// #52191 when on the records tab, only display permissions that include
		// the "View Records" option
		if (this.onlyViewRecords) {
			permissionIds = _.filter(permissionIds, id => {
				let permission = permissionsById[id]
				return permission.viewRecords
			})
		}

		this.$el.toggleClass('has-security-filters', !_.isEmpty(permissionIds))
		if (_.isEmpty(permissionIds)) {
			this.$('.security-filters').addClass('is-not-displayed')
			return
		}

		// check if there's a principal the user belongs to which doesn't have
		// any permission filters, in which case everything can be viewed
		// (#15308)
		if (this.hasUnfilteredPermission()) {
			this.$('.security-filters').addClass('is-not-displayed')
			return
		}

		_.invokeMap(this.securityFilters, 'remove')
		this.securityFilters = []
		let securityFiltersEl = this.$('.security-filters-content')
		_.each(permissionIds, (permissionId, index) => {
			let filters = this.filtersByPermissionId[permissionId] || []
			let securityFilter = new DatasetsFilterBadges({
				model: this.model,
				filters: filters,
				treeItemsById: this.treeItemsById,
				isEditable: false,
				showPermissionFilters: false,
				filterTextOptions: {
					showBuiltInRollupTreeGroupLabel : this.options.showBuiltInRollupTreeGroupLabel
				}
			})
			this.securityFilters.push(securityFilter)
			// if we're coming from a dataset series, we might be pointing at a
			// deleted permission in which case the security filter is no longer
			// tied to a particular principal but is nonetheless applied because
			// removing a permission shouldn't make more stuff visible!
			const principalLabel = _.get(permissionsById[permissionId], 'principal.label')
			if (index > 0) {
				securityFiltersEl.append(
					`<div class="separator-text query-separator-text" data-separator="${i18n.get('common.or')}"></div>`
				)
			}
			if (!_.isEmpty(principalLabel)) {
				securityFiltersEl.append(`<h4>${principalLabel}</h4>`)
			}

			securityFiltersEl.append(securityFilter.el)
			securityFilter.render()
		})
	},

	/**
	 * Returns whether there is a permission which doesn't have any filters associated with it.
	 * If one of these is found, that means none of the security filters should
	 * be displayed (#15308).
	 */
	hasUnfilteredPermission() {
		return this.model.get('hasUnfilteredPermission')
	},

	addFilter() {
		this.activeFilters.addFilter()
	},

	remove() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.activeFilters].concat(this.securityFilters), 'remove')
	}

})

export default DatasetFiltersView

DatasetFiltersView.__filename__ = 'datasets/dataset-filters.js'
